/**
 * The demo house's guest facts — what the welcome page writes, and the one
 * reader the guest chrome keys on (cards `t_2d4e1bc1` D9 / `t_912fe0ef` D10
 * write them, `t_803e86a4` D12 reads them).
 *
 * A visitor who walks in through the welcome page (`/demo`) becomes a throwaway
 * hub user whose role is `viewer`, and the page marks the browser before it
 * hands over to the app:
 *
 *     localStorage.demo_guest = '1'
 *
 * That flag is what "a guest is here" means before the identity has loaded: the
 * page writes it and then redirects, so it is already in storage when this
 * document boots, while `/users/me` is still a request in flight. The ROLE is
 * the app's own gate once the hub has answered (`isViewerRole`,
 * `utils/roles.ts`) — the flag only says which door this browser came through,
 * and never authorises anything: the hub's guard is the security layer (D9's
 * guest scope guard), and this module is honesty, not enforcement.
 *
 * Pure module: no store, no Vue, and the storage is a parameter — a test can
 * pass a fake, and a browser that refuses storage (private mode, blocked
 * cookies) reads as "no flag" instead of throwing on somebody's phone.
 */

/** What the welcome page writes when a visitor becomes a guest (D10). */
export const DEMO_GUEST_STORAGE_KEY = 'demo_guest';

/**
 * What the hint strip writes when the person closes it. Per device, not per
 * session: a guest who has read the sentence once has read it.
 */
export const DEMO_HINT_DISMISSED_KEY = 'demo_hint_dismissed';

/** Just enough of `Storage` for these readers. */
export interface FlagStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** The browser's own storage, or null where there is none (a test, SSR). */
function defaultStorage(): FlagStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // A browser that refuses access to storage throws on the property itself.
    return null;
  }
}

function readFlag(key: string, storage: FlagStorage | null): boolean {
  if (!storage) return false;
  try {
    return storage.getItem(key) === '1';
  } catch {
    return false;
  }
}

/** Did this browser come in through the guest door? */
export function readDemoGuestFlag(storage: FlagStorage | null = defaultStorage()): boolean {
  return readFlag(DEMO_GUEST_STORAGE_KEY, storage);
}

/** Has the accelerated-demo hint already been closed on this device? */
export function readDemoHintDismissed(storage: FlagStorage | null = defaultStorage()): boolean {
  return readFlag(DEMO_HINT_DISMISSED_KEY, storage);
}

/** Remember that the hint was closed. A storage that refuses writes just forgets. */
export function writeDemoHintDismissed(storage: FlagStorage | null = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.setItem(DEMO_HINT_DISMISSED_KEY, '1');
  } catch {
    // ignore — losing a dismissal is worth less than throwing mid-render
  }
}

/** A hub user, as far as a display name is concerned. */
export interface NameableUser {
  fullName?: string | null;
  name?: string | null;
  username?: string | null;
}

/**
 * The name the guest pill shows: what the person typed at the welcome page
 * (the hub stores it as `fullName`), then the app's own `name`, then the
 * generated `username`, then nothing — the pill then reads just "Guest".
 */
export function guestDisplayName(user: NameableUser | null | undefined): string {
  if (!user) return '';
  for (const candidate of [user.fullName, user.name, user.username]) {
    const value = typeof candidate === 'string' ? candidate.trim() : '';
    if (value) return value;
  }
  return '';
}
